/**
 * CDN media URLs and the browser HTTP cache.
 *
 * `cdn.littlestreamer.nl` (R2 behind Cloudflare) only emits
 * `Access-Control-Allow-Origin` when the request carries an `Origin` header —
 * and the response to a request *without* `Origin` carries no `Vary` header at
 * all. So a plain `<img src=...>` preview and a CORS read of the same URL
 * (`fetch()`, or an `<img crossOrigin="anonymous">` for canvas export) share a
 * single browser-cache entry. Whichever request runs first wins: when it is the
 * plain preview, the cached response has no `Access-Control-Allow-Origin`, and
 * every CORS read of that URL fails until the entry expires. The responses
 * carry no `Cache-Control`, so heuristic freshness can keep a poisoned entry
 * alive for days — which is why this looks like it "comes back" long after the
 * bucket CORS policy was last checked.
 *
 * Giving CORS reads their own URL gives them their own cache entry, one that is
 * only ever populated by a request that sent `Origin`. The marker is stable so
 * the entry stays cacheable across renders.
 */
const CORS_MARKER = 'cors';

export function corsMediaUrl(src: string): string {
  if (!src || /^(blob:|data:)/i.test(src)) return src;
  try {
    const parsed = new URL(src, window.location.href);
    // Same-origin assets never go through CORS; leave them on the shared entry.
    if (parsed.origin === window.location.origin) return src;
    parsed.searchParams.set(CORS_MARKER, '1');
    return parsed.toString();
  } catch {
    return src;
  }
}
